import moment from "moment";
import { manageLoginPop } from "../redux/common/slice";
import { myAccountRoute, privateRoutes } from "../routes/routesPath";
import {
  defaultDescription,
  femaleAvatarHolder,
  mainKeywords,
  maleAvatarHolder,
  routeMetadata,
} from "./constants";
import { format_time } from "./formatersAndParsers";
import { getStore } from "../redux/store";

export const getUserImage = (user) => {
  if (!user) return maleAvatarHolder;

  if (user?.avatar?.url) {
    return user.avatar.url;
  }
  if (user?.gender === "female") {
    return femaleAvatarHolder;
  }
  return maleAvatarHolder;
};

export const getMyAccountPagePath = (path) => {
  if (!path) return `${myAccountRoute}${privateRoutes.profileRoute}`;
  return `${myAccountRoute}${path}`;
};

export const handleLoginPop = (action) => {
  const store = getStore();
  const dispatch = store.dispatch;
  dispatch(manageLoginPop(action));
};

export const isArray = (array) => {
  if (Array.isArray(array)) return array;
  return [];
};

export const isEmpty = (data) => {
  if (!data) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") return Object.keys(data).length === 0;
  return false;
};

export const getSlug = (title) => {
  if (!title) return "";

  let slug = title
    .replace(/[^a-zA-Z0-9\s]/g, "")
    ?.toLowerCase()
    .split(" ")
    .filter(Boolean)
    ?.join("-")
    ?.slice(0, 30);

  return slug;
};

export const getCookie = (cookieString, key) => {
  if (!key || !cookieString) return "";

  const allCookies = cookieString?.split(";");
  const myCookie = allCookies?.find((cookie) => cookie?.trim()?.startsWith(key));

  if (!myCookie) return "";
  return myCookie.substr(key.length + 2);
};

export const asycnSetState = (state, setState) => {
  return Promise.resolve(setState(state));
};

export const getLastMessageDateTime = (date) => {
  if (!date || !moment(date).isValid()) return "";

  const newDate = new Date();
  const today = newDate.getDate();

  const msgDate = new Date(date);
  const msgDay = msgDate.getDate();

  if (msgDay === today) return format_time(date);
  else if (msgDay === today - 1) return "Yesterday";
  else return moment(date).format("DD MMM");
};

export const getOTPExpiryTime = (period = 120) => {
  const currentTime = new Date();
  const expiryTime = currentTime.setSeconds(currentTime.getSeconds() + period);
  return expiryTime;
};

export const formatReactionCount = (count) => {
  const reactionCount = Number(count);
  if (!reactionCount || isNaN(reactionCount)) return 0;

  if (count > 1000000000) return `${parseFloat((count / 1000000000).toFixed(2))}B`;
  if (count > 1000000) return `${parseFloat((count / 1000000).toFixed(2))}M`;
  if (count > 1000) return `${parseFloat((count / 1000).toFixed(1))}k`;
  return count;
};

export const getFilters = async (filtersList = [], selectedFilters = {}) => {
  const filters = {};

  for await (const filter of filtersList) {
    if (!selectedFilters[filter.name]) {
      filters[filter.name] = filter.defaultValue;
      continue;
    }

    if (typeof filter.type === "string") {
      filters[filter.name] = selectedFilters[filter.name].toString();
    } else if (filter.type === "number" && !isNaN(selectedFilters[filter.name])) {
      filters[filter.name] = Number(selectedFilters[filter.name]);
    } else {
      filters[filter.name] = filter.defaultValue;
    }
  }

  return filters;
};

export const getRouteMetaData = ({ htmlData = "", route = "/", metaData = {} }) => {
  const routeData = routeMetadata[route] || {};
  const WEBSITE_LINK = "https://feelinghub.in";

  const metaKeywords = [
    ...mainKeywords,
    ...(routeData.keywords || []),
    ...(metaData.keywords || []),
  ].join(", ");
  const metaTitle = metaData.title
    ? `${metaData.title} | FeelingHub`
    : routeData.title
    ? `${routeData.title} | FeelingHub`
    : "FeelingHub";
  const metaLink = `${process.env.REACT_APP_WEBSITE_NAME || WEBSITE_LINK}${
    metaData.link || routeData.link || ""
  }`;
  const metaRobots = routeData.noIndex ? "noindex, nofollow" : "index, follow";
  const appImage = `${process.env.REACT_APP_WEBSITE_NAME || WEBSITE_LINK}/ogImage.png`;
  const metaDescription = metaData.description || routeData.description || defaultDescription;

  const metadataReplacements = [
    { regex: /<title[^>]*>.*?<\/title>/is, replacement: `<title>${metaTitle}</title>` },
    {
      regex: /<meta name="description" content=".*?"\s*\/?>/is,
      replacement: `<meta name="description" content="${metaDescription}">`,
    },
    {
      regex: /<link rel="canonical" href=".*?"\s*\/?>/is,
      replacement: `<link rel="canonical" href="${metaLink}">`,
    },
    {
      regex: /<meta name="keywords" content=".*?"\s*\/?>/is,
      replacement: `<meta name="keywords" content="${metaKeywords}">`,
    },
    {
      regex: /<meta name="robots" content=".*?"\s*\/?>/is,
      replacement: `<meta name="robots" content="${metaRobots}">`,
    },
    // {
    //   regex: /<link rel="icon" href=".*?"\s*\/?>/is,
    //   replacement: `<link rel="icon" href="${appImage}">`,
    // },
    {
      regex: /<meta property="og:url" content=".*?"\s*\/?>/is,
      replacement: `<meta property="og:url" content="${metaLink}">`,
    },
    {
      regex: /<meta property="og:title" content=".*?"\s*\/?>/is,
      replacement: `<meta property="og:title" content="${metaTitle}">`,
    },
    {
      regex: /<meta property="og:description" content=".*?"\s*\/?>/is,
      replacement: `<meta property="og:description" content="${metaDescription}">`,
    },
    {
      regex: /<meta property="og:image" content=".*?"\s*\/?>/is,
      replacement: `<meta property="og:image" content="${appImage}">`,
    },
    {
      regex: /<meta property="og:image:alt" content=".*?"\s*\/?>/is,
      replacement: `<meta property="og:image:alt" content="Feelinghub">`,
    },
    {
      regex: /<meta property="twitter:url" content=".*?"\s*\/?>/is,
      replacement: `<meta property="twitter:url" content="${metaLink}">`,
    },
    {
      regex: /<meta property="twitter:title" content=".*?"\s*\/?>/is,
      replacement: `<meta property="twitter:title" content="${metaTitle}">`,
    },
    {
      regex: /<meta property="twitter:description" content=".*?"\s*\/?>/is,
      replacement: `<meta property="twitter:description" content="${metaDescription}">`,
    },
    {
      regex: /<meta property="twitter:image" content=".*?"\s*\/?>/is,
      replacement: `<meta property="twitter:image" content="${appImage}">`,
    },
    {
      regex: /<meta property="twitter:card" content=".*?"\s*\/?>/is,
      replacement: `<meta property="twitter:card" content="${appImage}">`,
    },
  ];

  let updatedMetaData = htmlData;
  metadataReplacements.forEach(({ regex, replacement }) => {
    if (updatedMetaData.match(regex)) {
      updatedMetaData = updatedMetaData.replace(regex, replacement);
    } else {
      updatedMetaData = updatedMetaData.replace("</head>", `${replacement}\n</head>`);
    }
  });

  return updatedMetaData;
};
